import { API_QR_CODE, API_CHECK_STATIC_QR_CODE } from "../../utils/apis";
import { Button, Form, Input, message, Modal, Spin } from "antd";
import styled from "styled-components";
import { BASE_API_URL } from "../../config";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useGlobalContext } from "../../context/GlobalContext";
import useSet from "../common/useSet";
import useGet from "../common/useGet";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export const StaticQRcode = () => {
  const { authToken } = useAuth();
  const { data: globalData, updateData } = useGlobalContext();
  const { openStaticQRCode } = globalData || {};
  const [form] = Form.useForm();
  const [existingQRCode, setExistingQRCode] = useState<any>(null);

  const {
    uploadData,
    submitting,
    error: submitError,
    success: submitSuccess,
    data: responseData,
  } = useSet();

  const {
    loadData: checkStaticQRCode,
    loading: checkingQRCode,
    data: qrCodeCheckData,
  } = useGet();

  const handleClose = () => {
    updateData({ openStaticQRCode: false });
    form.resetFields();
    setExistingQRCode(null);
  };

  const handleSubmit = (values: any) => {
    uploadData({
      api: API_QR_CODE,
      params: {
        type_of_payment: values.type_of_payment,
      },
    });
  };

  // Check for existing static QR code when modal opens
  useEffect(() => {
    if (openStaticQRCode) {
      checkStaticQRCode({ api: API_CHECK_STATIC_QR_CODE });
    }
  }, [openStaticQRCode]);

  // Process QR code check response
  useEffect(() => {
    if (qrCodeCheckData) {
      if (qrCodeCheckData.has_static_qr_code && qrCodeCheckData.qr_code) {
        setExistingQRCode(qrCodeCheckData.qr_code);
      } else {
        setExistingQRCode(null);
      }
    }
  }, [qrCodeCheckData]);

  const downloadPDF = async (id: string) => {
    try {
      const response = await axios({
        method: "GET",
        url: `${BASE_API_URL}${API_QR_CODE}${id}/generate-payment-pdf/`,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr_code_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully");
    } catch (error) {
      message.error("Error downloading PDF");
    }
  };

  useEffect(() => {
    if (submitSuccess && responseData) {
      toast.success("Static QR code generated successfully");
      updateData({ openStaticQRCode: false });
      form.resetFields();
      setExistingQRCode(null);
    }
  }, [submitSuccess, responseData]);

  useEffect(() => {
    if (
      submitError &&
      Object.keys(submitError).length > 0 &&
      !submitError.notSubmit
    ) {
      const errorMessage =
        (typeof submitError?.message === "string"
          ? submitError.message
          : null) ||
        (typeof submitError?.detail === "string" ? submitError.detail : null) ||
        "Failed to generate static QR code";
      toast.error(errorMessage);
    }
  }, [submitError]);

  return (
    <Modal
      open={!!openStaticQRCode}
      onCancel={handleClose}
      title={existingQRCode ? "Static QR Code" : "Generate Static QR Code"}
      footer={null}
      centered
      width={500}
    >
      {checkingQRCode ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#8c8c8c" }}>
            Checking for existing QR code...
          </p>
        </div>
      ) : existingQRCode ? (
        <QRCodeModalContent>
          <h3>Your Static QR Code</h3>
          <QRCodeImage
            src={existingQRCode?.qr_code_image}
            alt="Static QR Code"
          />
          <QRCodeData>{existingQRCode?.qr_code_data}</QRCodeData>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              marginTop: 24,
            }}
          >
            <Button onClick={handleClose} size="large">
              Close
            </Button>
            <Button
              type="primary"
              onClick={() => downloadPDF(existingQRCode.id)}
              size="large"
            >
              Download PDF
            </Button>
          </div>
        </QRCodeModalContent>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          initialValues={{
            type_of_payment: "static",
          }}
        >
          <Form.Item label="Type of Payment" name="type_of_payment">
            <Input disabled placeholder="static" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <Button onClick={handleClose} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                size="large"
              >
                Generate Static QR Code
              </Button>
            </div>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

// Styled Components for QR Code Modal
const QRCodeModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;

  h3 {
    margin-bottom: 20px;
    color: #262626;
    font-size: 18px;
  }

  p {
    color: #595959;
  }
`;

const QRCodeImage = styled.img`
  max-width: 300px;
  width: 100%;
  height: auto;
  margin: 20px 0;
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 10px;
  background: white;
`;

const QRCodeData = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  word-break: break-all;
  max-width: 400px;
  margin-top: 10px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
`;
